import { unescapeQuotesInObject } from '~h/text';
import metrikaGoal from '~/helpers/metrikaGoal';
import { getKLDARByCityNameWithDadata } from '~h/cityCodes';

import { formatPhoneToNumber } from '~h/format-phone';
import routes from '~/apiRouter';

export const mapCompanyToRawData = company => {
  return {
    COMPANY_NAME: company.companyName ?? '',
    COMPANY_ID: company.id ?? '',
    INN: company.inn ?? '',
    KPP: company.kpp ?? '',
    CITY: company.city ?? '',
    CITY_CODE: company.cityCode ?? '',
    BANK_ACCOUNT: company.paymentAccount ?? '',
    CORR_ACCOUNT: company.paymentCorAccount ?? '',
    BIK: company.bankBik ?? '',
    BANK: company.bankName ?? '',
    LEGAL_ADDRESS: company.address ?? '',
    LOCATION_ADDRESS: company.factAddress ?? '',
  };
};

export const registerMetrics = () => {
  metrikaGoal('reg-click');
  metrikaGoal('scenario-search-reg-click');
  metrikaGoal('scenario-menu-reg-click');
  metrikaGoal('scenario-slider-reg-click');
  metrikaGoal('scenario-playbill-reg-click');
};

export const checkIsAuthed = () => {
  return Boolean(
    window.user &&
      (window.user.LOGIN || window.user.TELEPHONE || window.user.CONTACT_ID),
  );
};

export const unescapeUserData = array => {
  return array?.map(item => unescapeQuotesInObject(item)) || array;
};

export const eventTypes = {
  FREE: 'FREE',
  CONSULT: 'CONSULT',
  EDU: 'EDU',
};

export const getCity = () => {
  if (typeof window.city !== 'undefined') {
    const currentCity = Object.values(window.city).find(item => {
      return item.CURRENT === true;
    });
    return { cityName: currentCity.NAME, cityCode: currentCity.KLADR };
  }

  return {};
};

export const decodeWindowUser = user => {
  return {
    phone: user?.TELEPHONE,
    email: user?.EMAIL || undefined,
    name: user?.FIRST_NAME || undefined,
    lastName: user?.LAST_NAME || undefined,
    patronymic: user?.MIDDLE_NAME || undefined,
    expertise: user?.SPHERE_OF_ACTIVITY || undefined,
    company: user?.COMPANIES.length
      ? user?.COMPANIES[user?.COMPANIES.length - 1].COMPANY_NAME
      : user?.COMPANY_NAME || undefined,
    companies: user?.COMPANIES || undefined,
    companyPosition: user?.JOB_TITLE || undefined,
    addressOfRegistration: user?.REGISTRATION_ADDRESS || undefined,
    birthday: user?.BIRTH_DATE.slice(0, 10) || undefined,
    passport: user?.PASSPORT_NUMBER || undefined,
    passportCreated: user?.PASSPORT_ISSUE_DATE.slice(0, 10) || undefined,
    passportPlaceCreated: user?.PASSPORT_AUTHORITY || undefined,
    subscribe: user?.MKT_SUBSCRIPTION || undefined,
    registrationAddress: user?.REGISTRATION_ADDRESS || undefined,
    cityCode: user?.CITY_CODE || undefined,
    citizenship: user?.CITIZENSHIP || 'Российская Федерация',
    citizenshipCode: user?.CITIZENSHIP_CODE || 'RU',
    snils: user?.SNILS || undefined,
    diplomaRegNumber: user?.DIPLOMA_REG_NUMBER || undefined,
    diplomaQualification: user?.DIPLOMA_QUALIFICATION || undefined,
    diplomaSeries: user?.DIPLOMA_SERIES || undefined,
    diplomaNumber: user?.DIPLOMA_NUMBER || undefined,
    type: user?.SITE?.TYPE || undefined,
  };
};

export const parseCompanyDadata = dadataValue => {
  return {
    companyName: dadataValue.value || undefined,
    inn: dadataValue.data.inn || undefined,
    kpp: dadataValue.data.kpp || undefined,
    city: dadataValue.data.address.data.city || undefined,
    cityCode: dadataValue.data.address.data.city_kladr_id || undefined,
    address: dadataValue.data.address.unrestricted_value || undefined,
    factAddress: dadataValue.data.address.unrestricted_value || undefined,
  };
};

export const passportPatterns = [
  {
    citizenshipCode: 'RU',
    mask: '9999 999999',
  },
];

export const registerCompanyRequest = async company => {
  const {
    address,
    bankBik,
    bankName,
    city,
    cityCode,
    companyName,
    factAddress,
    inn,
    kpp,
    paymentAccount,
    paymentCorAccount,
    currentLead,
    signer,
  } = company;

  const preparedCityCode =
    cityCode || (await getKLDARByCityNameWithDadata(city));

  const options = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    credentials: 'include',
  };

  const companyBody = {
    COMPANY_FULLNAME: companyName,
    COMPANY_NAME: companyName,
    CITY: city,
    CITY_CODE: preparedCityCode,
    INN: inn,
    KPP: kpp,
    BANK_ACCOUNT: paymentAccount,
    CORR_ACCOUNT: paymentCorAccount,
    BANK: bankName,
    BIK: bankBik,
    LEGAL_ADDRESS: address,
    LOCATION_ADDRESS: factAddress,
    IS_CONTACT_A_SIGNER: false,
    REGISTRATION_ID: currentLead,
  };

  if (signer) {
    const {
      basis,
      companyPosition,
      lastName,
      name,
      patronymic,
      proxyDate,
      proxyNum,
    } = signer;

    const signerBody = {
      IS_CONTACT_A_SIGNER: 'false',
      SIGNER_LAST_NAME: lastName,
      SIGNER_FIRST_NAME: name,
      SIGNER_MIDDLE_NAME: patronymic,
      SIGNER_JOB_TITLE: companyPosition,
      SIGNER_BASIS: basis === 'Устав' ? '0' : '1',
      ATTORNEY_DATE: proxyDate || undefined,
      ATTORNEY_NUMBER: proxyNum || undefined,
    };

    options.body = JSON.stringify({ ...companyBody, ...signerBody });
  } else {
    options.body = JSON.stringify(companyBody);
  }

  const token = localStorage.getItem('token');
  if (token) options.headers.Authorization = token;

  const response = await fetch(`${routes.user.company}`, options);

  return response.json();
};

export const createLeadRequest = async (userData = {}, eventCode, pageCode) => {
  const body = {
    EVENT_CODES: [],
    EVENT_PAGE: eventCode,
    PAGE_CODE: pageCode,
    TELEPHONE: formatPhoneToNumber(userData.phone) || undefined,
    EMAIL: userData.email || undefined,
    NAME: userData.name || undefined,
    LAST_NAME: userData.lastName || undefined,
    SECOND_NAME: userData.patronymic || undefined,
    CITY_CODE: userData.cityCode || undefined,
    COMPANY_NAME: userData.company || undefined,
    JOB_TITLE: userData.companyPosition || undefined,
    TYPE: userData.type || undefined,
  };

  const options = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  };

  const response = await fetch(`${routes.user.lead}`, options);
  return response.json();
};

export const getDadataCompanies = async companyName => {
  const options = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      authorization: `Token 318c0272e1d69ceed262f4345982fc0bce695feb`,
    },
    body: JSON.stringify({
      query: companyName,
      count: 10,
      status: ['ACTIVE'],
    }),
  };

  const response = await fetch(
    'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/party',
    options,
  );
  return response.json();
};

export const getCompanyByName = async companyName => {
  const dadataRes = await getDadataCompanies(companyName);
  if (dadataRes.suggestions[0]) {
    return parseCompanyDadata(dadataRes.suggestions[0]);
  } else {
    return undefined;
  }
};

export const registerStudents = async (
  studentsList,
  options,
  signer = null,
  eventTypes,
) => {
  const fetchOptions = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    credentials: 'include',
  };

  const token = localStorage.getItem('token');
  if (token) fetchOptions.headers.Authorization = token;

  const {
    subscribe,
    promo,
    eventCodes,
    companyId,
    pageCode,
    isSelfReg,
    isCompanyReg,
    currentLead,
  } = options;

  const registrationRequests = studentsList.map(async student => {
    const studentBody = {
      PROMO_CODE: promo || null,
      EVENT_CODES: eventCodes,
      PAGE_CODE: pageCode,
      TELEPHONE: student.phone ? formatPhoneToNumber(student.phone) : undefined,
      EMAIL: student?.email || undefined,
      FIRST_NAME: student?.name || undefined,
      LAST_NAME: student?.lastName || undefined,
      MIDDLE_NAME: student?.patronymic || undefined,

      COMPANY_NAME: student?.company || undefined,
      JOB_TITLE: student?.companyPosition || undefined,
      SPHERE_OF_ACTIVITY: student?.expertise || undefined,
      PASSPORT_AUTHORITY: student?.passportPlaceCreated || undefined,
      PASSPORT_ISSUE_DATE: student?.passportCreated || undefined,
      PASSPORT_NUMBER: student?.passport || undefined,
      REGISTRATION_ADDRESS: student?.addressOfRegistration || undefined,
      CITIZENSHIP: student?.citizenship || undefined,
      CITIZENSHIP_CODE: student?.citizenshipCode || undefined,

      BIRTH_DATE: student?.birthday || undefined,
      MKT_SUBSCRIPTION: subscribe || undefined,

      CURRENT_LEAD: currentLead || undefined,
      COMPANY_ID: companyId || undefined,
    };

    if (signer) {
      const {
        basis,
        companyPosition,
        lastName,
        name,
        patronymic,
        proxyDate,
        proxyNum,
      } = signer;

      const signerBody = {
        IS_CONTACT_A_SIGNER: 'false',
        SIGNER_LAST_NAME: lastName,
        SIGNER_FIRST_NAME: name,
        SIGNER_MIDDLE_NAME: patronymic,
        SIGNER_JOB_TITLE: companyPosition,
        SIGNER_BASIS: basis === 'Устав' ? '0' : '1',
        ATTORNEY_DATE: proxyDate || undefined,
        ATTORNEY_NUMBER: proxyNum || undefined,
      };

      fetchOptions.body = JSON.stringify({ ...studentBody, ...signerBody });
    } else {
      fetchOptions.body = JSON.stringify(studentBody);
    }

    const url =
      isSelfReg && !isCompanyReg
        ? routes.user.listener
        : routes.user.listenerOther;

    if (url === routes.user.listenerOther) {
      if (eventTypes === 'FREE') metrikaGoal('regFreeOther');
      if (eventTypes === 'CONSULT') metrikaGoal('regConsultOther');
      if (eventTypes === 'EDU') metrikaGoal('regEduOther');
    }

    const response = await fetch(url, fetchOptions);

    return response.json();
  });

  return Promise.all(registrationRequests);
};

export const sentUsersBrowserData = async ({ ...userData }) => {
  const { isNewsSubscribe, isPolicyAgreed } = userData;
  let phone;
  if (userData?.phone) {
    phone = userData.phone;
  }
  const formattedFormPhone = phone ? formatPhoneToNumber(phone) : null;
  const browserData = {};
  for (let i in navigator) {
      browserData[i] = navigator[i];
  };

  if (isNewsSubscribe || !isPolicyAgreed) {
    fetch('https://geolocation-db.com/json/')
      .then(data => data.json())
      .then(res => ({
        IP: res.IPv4,
        FORM_PHONE: formattedFormPhone || '',
        USER_PHONE: window.user.TELEPHONE || '',
        BROWSER: browserData,
        TYPE: 'SUBSCRIBE',
      }))
      .then(async data => {
         const options = {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(data),
        };

        await fetch(`${routes.user.agreement}`, options);
      });
  }

  if (
    (isPolicyAgreed && !isNewsSubscribe) ||
    (isPolicyAgreed && isNewsSubscribe)
  ) {
    fetch('https://geolocation-db.com/json/')
      .then(data => data.json())
      .then(res => ({
        IP: res.IPv4,
        FORM_PHONE: formattedFormPhone || '',
        USER_PHONE: window.user.TELEPHONE || '',
        BROWSER: browserData,
        TYPE: 'PRIVACY',
      }))
      .then(async data => {
        const options = {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(data),
        };

        await fetch(`${routes.user.agreement}`, options);
      });
  } 
};
